import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { Form, Input, Card, Typography, Divider, Upload, Avatar } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, LockOutlined, CameraOutlined } from '@ant-design/icons';
import { Button } from '@bancobarigui/barihaus';
import { useProfile } from './useProfile';

const { Title, Text } = Typography;

function ProfileContent() {
  const { form, loading, uploading, photoUrl, handleSubmit, handlePhotoUpload } = useProfile();

  return (
    <div className="flex flex-col gap-6">
      <Card title="Informações Pessoais" className="shadow-sm">
        <div className="flex flex-col items-center mb-6">
          <Avatar
            size={120}
            icon={<UserOutlined />}
            src={photoUrl}
            className="mb-4"
          />
          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              handlePhotoUpload(file);
              return false;
            }}
            accept="image/*"
          >
            <Button
              icon={<CameraOutlined />}
              loading={uploading}
              disabled={loading}
            >
              {photoUrl ? 'Alterar Foto' : 'Adicionar Foto'}
            </Button>
          </Upload>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={loading}
        >
          <Form.Item
            label="Nome"
            name="name"
            rules={[{ required: true, message: 'Por favor, insira seu nome' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Seu nome completo"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Por favor, insira seu email' },
              { type: 'email', message: 'Email inválido' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="seu@email.com"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Telefone"
            name="phoneNumber"
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="(00) 00000-0000"
              size="large"
            />
          </Form.Item>

          <Divider />

          <Title level={4}>Alterar Senha</Title>
          <Text type="secondary" className="block mb-4">
            Deixe em branco se não deseja alterar a senha
          </Text>

          <Form.Item
            label="Senha Atual"
            name="currentPassword"
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Digite sua senha atual"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Nova Senha"
            name="newPassword"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value && !getFieldValue('currentPassword')) {
                    return Promise.resolve();
                  }
                  if (!getFieldValue('currentPassword')) {
                    return Promise.reject(new Error('Digite a senha atual primeiro'));
                  }
                  if (value && value.length < 6) {
                    return Promise.reject(new Error('A senha deve ter no mínimo 6 caracteres'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Digite sua nova senha"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Confirmar Nova Senha"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value && !getFieldValue('newPassword')) {
                    return Promise.resolve();
                  }
                  if (getFieldValue('newPassword') && !value) {
                    return Promise.reject(new Error('Por favor, confirme a nova senha'));
                  }
                  if (value && value !== getFieldValue('newPassword')) {
                    return Promise.reject(new Error('As senhas não coincidem'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirme sua nova senha"
              size="large"
            />
          </Form.Item>

          <Form.Item className="mb-0 mt-6">
            <Button
              buttonColor="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              block
            >
              Salvar Alterações
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export function Profile() {
  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title="Meu Perfil"
          subtitle="Visualize e edite suas informações pessoais"
        />
      }
    >
      <FeatureAlert services={['user']} />
      <ServiceCheck
        services={['user']}
        featureName="Meu Perfil"
      >
        <ProfileContent />
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
